import { useCallback, useState, useEffect, useRef, JSX } from 'react';

import { saveWorkingProjectByFields } from '@/store/slices/project';
import { MarketingOptInTypes } from '@/types/registration.types';
import { ICONS } from '@ui/icons/types';
import Popover from '@ui/popover/popover';
import RotatableIcon from '@ui/RotatableIcon/rotatable-icon';
import {
  selectIsSavingWorkingProject,
  selectWorkingProjectRegistrationQuestions,
  selectWorkingProjectRequiredRegistrationQuestions,
} from '@/store/slices/project/selectors';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import Text from '@ui/text/text/text';
import Button from '@ui/buttons/button/button';
import Container from '@ui/container/container';
import useComputedRegistrationSettings from '../../hooks/use-computed-registration-settings';
import toastTrigger from '@ui/toast/toast-trigger';
import { EToastTypes } from '@ui/toast/toast.types';
import { isCustomSsoEnabledForSettings } from '@/utils/authentication/sso-utils';
import TooltipPortal from '@ui/tooltip-portal/tooltip-portal';
import { TooltipPortalContentDirection, TooltipPortalPosition } from '@ui/tooltip-portal/tooltip-portal-types';
import { AuthFlowMode, deriveRegistrationUpdate } from '../../helpers/derive-registration-update';

type TOptionKeys = AuthFlowMode;
type TSelectOption = { label: string; value: TOptionKeys };
const OPTIONS = new Map<TOptionKeys, TSelectOption>([
  ['native', { label: 'Native Registration', value: 'native' }],
  ['sso', { label: 'SSO', value: 'sso' }],
  ['thirdparty', { label: '3rd Party Registration', value: 'thirdparty' }],
]);

const AccessTypeSelector = () => {
  const portalEl = useRef<HTMLDivElement | null>(null);
  const savingWorkingProject = useAppSelector(selectIsSavingWorkingProject);

  const [selectedValue, setSelectedValue] = useState<TSelectOption | undefined>(OPTIONS.get('native'));

  const { passwordGateEnabled, courseOn, registrationSteps, registrationSettings } = useComputedRegistrationSettings();
  const registrationQuestions = useAppSelector(selectWorkingProjectRegistrationQuestions);
  const requiredRegistrationQuestions = useAppSelector(selectWorkingProjectRequiredRegistrationQuestions);

  const dispatch = useAppDispatch();

  useEffect(() => {
    if (registrationSettings?.externalRegistration?.isOn) {
      setSelectedValue(OPTIONS.get('thirdparty'));
    } else if (isCustomSsoEnabledForSettings(registrationSettings)) {
      // singleSignOn.isOn can be true under two scenarios:
      //  1. Custom SSO mode (SAML/OIDC/oAuth) — flips dropdown to "SSO"
      //  2. Native + social sign-in (Google) — keeps dropdown on "Native" since the social
      //     toggle lives inside the Native panel.
      setSelectedValue(OPTIONS.get('sso'));
    } else {
      setSelectedValue(OPTIONS.get('native'));
    }
  }, [registrationSettings]);

  const onChange = useCallback(
    (option?: TSelectOption) => {
      const value = option?.value;
      if (!registrationSettings) return;
      if (value === selectedValue) return;
      if (!value) return;

      setSelectedValue(option);

      // events-3 parity: turning SSO on disables marketing opt-in. Surface the change so the
      // admin knows their previous opt-in choice was reset. Toast lives here (not in the transform)
      // because it's UI feedback, not state derivation.
      const wasMarketingOptInActive =
        !!registrationSettings.marketing_email_options &&
        registrationSettings.marketing_email_options !== MarketingOptInTypes.noOptIn;
      if (value === 'sso' && wasMarketingOptInActive) {
        toastTrigger('Marketing email opt-in has been disabled', {
          type: EToastTypes.info,
          autoClose: 7000,
          description: 'In order to enable the marketing email option, you must disable SSO.',
        });
      }

      const update = deriveRegistrationUpdate(
        {
          registration_settings: registrationSettings,
          registration_steps: registrationSteps,
          registration_questions: registrationQuestions,
          required_registration_questions: requiredRegistrationQuestions,
        },
        { kind: 'auth-flow', mode: value },
        []
      );

      dispatch(saveWorkingProjectByFields(update));
    },
    [
      dispatch,
      registrationQuestions,
      registrationSettings,
      registrationSteps,
      requiredRegistrationQuestions,
      selectedValue,
    ]
  );

  return (
    <Popover
      cycleWithArrowKeys
      width="100%"
      closeOnClick
      closeOnEnter
      disabled={savingWorkingProject || !registrationSettings}
      refocusTriggerOnClose
      portalEl={portalEl}
      trackOnWindowScroll
      renderTriggerButton={({ open, closing }) => {
        const loading = savingWorkingProject || !registrationSettings;
        return (
          <Button
            clear
            height="32px"
            width="212px"
            padding="8px 16px"
            skLoader={{
              height: 30,
              width: 210,
              top: 1,
              left: 1,
              opacity: 0.8,
              loading,
              radius: 15,
            }}
            border={`1px solid ${open && !closing && !loading ? 'var(--global-ico-text-2)' : 'var(--global-ico-text-3)'}`}
            hoverBorder={`1px solid var(--global-ico-text-2)`}
            borderRadius="100px"
            className="flex ai-center jc-space-between gap-8"
          >
            <Text className="P2" design="GlobalIcoText1">
              {selectedValue?.label || ''}
            </Text>
            <RotatableIcon
              color="var(--global-ico-text-1)"
              name={ICONS.KEYBOARD_ARROW_DOWN}
              size={12}
              rotate={open && !closing ? '180deg' : '0'}
            />
          </Button>
        );
      }}
    >
      {({ triggerBounds, boxShadow }) => {
        const triggerWidth = triggerBounds?.width || 32;
        const leftPosition = triggerBounds?.left || 0;
        const transform = `translate(${`calc(-100% + ${triggerWidth}px)`}, ${(triggerBounds?.height || 32) + 2}px)`;
        return (
          <Container
            design="GlobalCont1"
            width="212px"
            className="position-fixed z-20 overflow-y-auto flex-column"
            borderRadius="12px"
            boxShadow={boxShadow}
            style={{
              top: (triggerBounds?.top || 0) + 2,
              left: leftPosition,
              transform,
            }}
          >
            <OptionWrapper option={OPTIONS.get('native')} onSelect={onChange} />
            <OptionWrapper
              option={OPTIONS.get('sso')}
              onSelect={onChange}
              disabled={passwordGateEnabled || courseOn}
              DisabledTooltip={
                <Text design="GlobalIcoText1" className="P2">
                  <div className="margin-b-8">SSO cannot be turned on with:</div>
                  <ul className="clean-list">
                    {passwordGateEnabled ? <li>- Password gating</li> : null}
                    {courseOn ? <li>- Courses</li> : null}
                  </ul>
                </Text>
              }
            />
            {/* Note: 3rd Party Registration was previously known as "disable native registration" */}
            <OptionWrapper option={OPTIONS.get('thirdparty')} onSelect={onChange} />
          </Container>
        );
      }}
    </Popover>
  );
};

export default AccessTypeSelector;

const OptionWrapper = ({
  option,
  onSelect,
  disabled,
  DisabledTooltip,
}: {
  option?: TSelectOption;
  onSelect: (option?: TSelectOption) => void;
  disabled?: boolean;
  DisabledTooltip?: JSX.Element;
}) => {
  if (!option) return null;
  return (
    <TooltipPortal
      disabled={!disabled || !DisabledTooltip}
      position={TooltipPortalPosition.LEFT}
      contentDirection={TooltipPortalContentDirection.RTL}
      tooltipContent={<>{DisabledTooltip}</>}
      childrenWrapperClass="width-100-percent"
    >
      <Button
        height="32px"
        padding="8px 16px"
        width="100%"
        clear
        disabled={disabled}
        disabledOpacity={0.3}
        type="submit"
        backgroundColor="transparent"
        hoverBackgroundColor="var(--global-cont-2)"
        onClick={() => onSelect(option)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            onSelect(option);
          }
        }}
      >
        <Container design="GlobalCont1" inheritButtonHover>
          <Text className="S3" design="GlobalIcoText1">
            {option.label}
          </Text>
        </Container>
      </Button>
    </TooltipPortal>
  );
};
